// Wrap notes from the call sheet tool (2F, R6.5): read only, one block per
// sheet, under Tasks and above Deliverables on desktop and phone. The call
// sheet owns the words; Henry edits them there.

import { Link } from 'react-router-dom';
import './WrapNotesCard.css';
import { melbourneDate } from '../../shared/dates';
import type { ProjectWrapNotes } from '../../shared/types';
import { formatDayMonth } from '../lib/calendar';
import { callsheetHref } from '../lib/toolEmbed';
import { Group } from './primitives';

const dayMonth = (ms: number) => formatDayMonth(melbourneDate(ms));
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function WrapNotesCard({ notes, mobile = false }: { notes: ProjectWrapNotes[]; mobile?: boolean }) {
  if (notes.length === 0) return null;
  const single = notes.length === 1;
  const missed = single ? notes[0]!.missed_shots.length : 0;
  const count = single
    ? missed > 0 && <span className="group-count wn-count-missed">{plural(missed, 'missed shot')}</span>
    : <span className="group-count">{notes.length} sheets</span>;
  return (
    <Group title="Wrap notes" link={count || undefined}>
      {notes.map((sheet) => <WrapNotesBlock key={sheet.callsheet_id} sheet={sheet} titled={!single} mobile={mobile} />)}
    </Group>
  );
}

function WrapNotesBlock({ sheet, titled, mobile }: { sheet: ProjectWrapNotes; titled: boolean; mobile: boolean }) {
  const fields = ([['Production', sheet.production], ['Footage', sheet.footage], ['Issues', sheet.issues]] as const)
    .filter(([, value]) => value.trim());
  const added = sheet.crew_added_shots;
  const missed = sheet.missed_shots;
  const wrapped = dayMonth(sheet.created_at);
  // A re-wrap replaces the block in place; the date says it moved on.
  const updated = sheet.updated_at > sheet.created_at ? ` · updated ${dayMonth(sheet.updated_at)}` : '';
  return (
    <div className="wn">
      {titled && (
        <div className="wn-sheet"><span className="t">{sheet.callsheet_title}</span><span className="m">{wrapped}{updated}</span></div>
      )}
      {fields.length > 0 && (
        <div className="wn-fields">
          {fields.map(([label, value]) => (
            <div className="wn-field" key={label}><span className="wn-k">{label}</span><span className="wn-v">{value}</span></div>
          ))}
        </div>
      )}
      {(added.length > 0 || missed.length > 0) && (
        <div className="wn-shots">
          {added.length > 0 && (
            <div>
              <div className="wn-k">Added by crew <span className="n">{added.length}</span></div>
              {added.map((shot) => (
                <div className="shot" key={shot.id} title={shot.added_by ? `Added by ${shot.added_by}` : undefined}>
                  <span className="dot" />{shot.description}
                </div>
              ))}
            </div>
          )}
          {missed.length > 0 && (
            <div>
              <div className="wn-k missed">Missed <span className="n">{missed.length}</span></div>
              {missed.map((shot) => <div className="shot missed" key={shot.id}><span className="dot" />{shot.description}</div>)}
            </div>
          )}
        </div>
      )}
      <div className="wn-foot">
        <span>From the call sheet · {wrapped}{titled ? '' : updated}</span>
        <Link className="r" to={callsheetHref(sheet.callsheet_url)}>{mobile ? 'Open ↗' : 'Open call sheet ↗'}</Link>
      </div>
    </div>
  );
}
